/**
 * Hook: useWebhookRawIngest
 * 
 * Consulta os registros de ingestão bruta de webhooks via RPC server-side.
 * Suporta filtros por provider, status, telefone, evento, instance e período.
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

/** Filtros disponíveis para consulta de raw ingest */
export interface WebhookRawIngestFilters {
  provider_slug?: string;
  resolution_status?: string;
  phone?: string;
  event_type?: string;
  instance_id?: string;
  start_date?: string;
  end_date?: string;
  limit?: number;
}

/** Estrutura de um registro retornado pela RPC */
export interface WebhookRawIngestRecord {
  id: string;
  provider_slug: string;
  instance_id: string | null;
  event_type: string | null;
  phone: string | null;
  resolution_status: string;
  discard_reason: string | null;
  processing_duration_ms: number | null;
  raw_payload: Record<string, unknown>;
  received_at: string;
  inbox_id: string | null;
  ip_address: string | null;
  tenant_id: string | null;
}

/**
 * Hook para buscar registros de webhook_raw_ingest via RPC
 * Todos os filtros são opcionais e processados server-side
 */
export function useWebhookRawIngest(filters: WebhookRawIngestFilters) {
  return useQuery({
    queryKey: ['webhook-raw-ingest', filters],
    queryFn: async () => {
      const params: Record<string, unknown> = {
        p_limit: filters.limit || 100,
      };

      // Adiciona filtros opcionais apenas quando preenchidos
      if (filters.provider_slug) params.p_provider_slug = filters.provider_slug;
      if (filters.resolution_status) params.p_resolution_status = filters.resolution_status;
      if (filters.phone) params.p_phone = filters.phone;
      if (filters.event_type) params.p_event_type = filters.event_type;
      if (filters.instance_id) params.p_instance_id = filters.instance_id;
      if (filters.start_date) params.p_start_date = filters.start_date;
      if (filters.end_date) params.p_end_date = filters.end_date;

      const { data, error } = await supabase.rpc('get_webhook_raw_ingest', params as any);

      if (error) throw error;
      return (data || []) as WebhookRawIngestRecord[];
    },
    refetchInterval: 30000, // Auto-refresh a cada 30s
  });
}
